/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */

import type { Action, Dispatch } from 'redux';

import Utils from '../../../utils/utils';
import {
  ADD_CONTAINER_ITEM,
  REPLACE_CONTAINER_ITEM,
  REMOVE_CONTAINER_ITEM,
  MERGE_DATA,
  ADD_SHARED_DATA
} from '../actions';
import { removeSharedData } from '../actions/action-creators';
import { dispatch } from '../state-manager';
import Constants from '../../../constants';

/**
 * {
 *    D_UserDefinedPage: {
 *      D_UserDefinedPage_2hash2: ["app/primary_1", "app/primary_2/secondary_1"],
 *    }
 *    context: {
 *      "app/primary_1": ["D_UserDefinedPage_2hash2"],
 *      "app/primary_2/secondary_1": ["D_UserDefinedPage_2hash2"]
 *    }
 * }
 */
const sharedPageUsageMap = {};

/**
 * this method helps to update the shared page usage per context in map
 * @param dataPageName - qualified data page name
 * @param dataPageNameWithHash - data page name with hash value
 * @param context - context name
 *
 * @private
 */
const updateSharedPageUsage = (dataPageName: string, dataPageNameWithHash: string, context: string) => {
  const prevDataPagesList = Utils.getIn(sharedPageUsageMap, [Constants.CONTEXT, context]);

  const dataPage = Utils.getIn(sharedPageUsageMap, [dataPageName]);

  if (dataPage) {
    const contextsList = Utils.getIn(sharedPageUsageMap, [dataPageName, dataPageNameWithHash], []);
    Utils.setIn(sharedPageUsageMap, [dataPageName, dataPageNameWithHash], [...new Set([...contextsList, context])]);
  } else {
    Utils.setIn(sharedPageUsageMap, [dataPageName, dataPageNameWithHash], [context]);
  }

  if (prevDataPagesList) {
    Utils.setIn(
      sharedPageUsageMap,
      [Constants.CONTEXT, context],
      [...new Set([...prevDataPagesList, dataPageNameWithHash])]
    );
  } else {
    Utils.setIn(sharedPageUsageMap, [Constants.CONTEXT, context], [dataPageNameWithHash]);
  }
};

/**
 * this method helps to remove the shared page usage per context in map
 * @param context - context name
 *
 * @private
 */
const removePreviousSharedDataPageMappings = (context: string) => {
  const prevDataPagesList = Utils.getIn(sharedPageUsageMap, [Constants.CONTEXT, context], []);

  const dataPagesToBeRemoved: any[] = [];
  prevDataPagesList.forEach((prevDataPageWithHash: any) => {
    const prevDataPageName = prevDataPageWithHash.substr(0, prevDataPageWithHash.lastIndexOf('_'));
    const contextsList = Utils.getIn(sharedPageUsageMap, [prevDataPageName, prevDataPageWithHash], []);
    const index = contextsList && contextsList.length > 0 && contextsList.indexOf(context);
    if (index > -1) {
      contextsList.splice(index, 1);
    }

    if (!contextsList || (contextsList && contextsList.length === 0)) {
      Utils.deleteIn(sharedPageUsageMap, [prevDataPageName, prevDataPageWithHash]);

      const currentDataPage = Utils.getIn(sharedPageUsageMap, [prevDataPageName]);
      if (currentDataPage && Object.keys(currentDataPage).length === 0) {
        Utils.deleteIn(sharedPageUsageMap, [prevDataPageName]);
      }

      dataPagesToBeRemoved.push({
        dataPageName: prevDataPageName,
        dataPageNameWithHash: prevDataPageWithHash
      });
    }
  });

  Utils.deleteIn(sharedPageUsageMap, [Constants.CONTEXT, context]);
  const contextMap = Utils.getIn(sharedPageUsageMap, [Constants.CONTEXT]);
  if (!contextMap || (contextMap && Object.keys(contextMap).length === 0)) {
    Utils.deleteIn(sharedPageUsageMap, [Constants.CONTEXT]);
  }

  if (dataPagesToBeRemoved.length > 0) {
    dispatch(removeSharedData({ dataPagesToBeRemoved }));
  }
};

/**
 * this method helps to update the shared pages usage per context in map
 * @param sharedDataPages - shared pages object
 * @param context - context name
 *
 * @private
 */
// eslint-disable-next-line @typescript-eslint/default-param-last
const updateSharedPagesUsage = (sharedDataPages: object = {}, context: string) => {
  const dataPageKeys = Object.keys(sharedDataPages);

  dataPageKeys.forEach((dataPageKey) => {
    const dataPage = Utils.getIn(sharedDataPages, [dataPageKey], {});
    const dataPageHashKeys = Object.keys(dataPage);
    dataPageHashKeys.forEach((dataPageWithHash) => {
      updateSharedPageUsage(dataPageKey, dataPageWithHash, context);
    });
  });
};

/**
 * this method helps to provide the shared pages usage map
 * @private
 *
 * @returns sharedPageUsageMap
 */
export const getSharedPageUsageMap = () => {
  return sharedPageUsageMap;
};

/**
 * this method handles SharedDataPage for child contexts
 * @param {string} type - action type
 * @param {string[]} childContainersContexts - list of child container context
 * @private
 */
const handleSharedDataPageForChildContexts = (type: string, childContainersContexts: string[]) => {
  if (
    childContainersContexts &&
    childContainersContexts.length > 0 &&
    (type === REPLACE_CONTAINER_ITEM || type === REMOVE_CONTAINER_ITEM)
  ) {
    childContainersContexts.forEach((childContext: string) => {
      removePreviousSharedDataPageMappings(childContext);
    });
  }
};

/**
 * Redux middleware to track the redux changes
 * and manipulate the shared data
 * @param store the store that will be affected
 */
const DataPageMiddleware = () => (next: Dispatch) => (action: Action) => {
  const actions = Array.isArray(action) ? action : [action];

  actions.forEach((actionItem) => {
    const { payload: { data = {}, context = null, childContainersContexts = [] } = {}, type } = actionItem;

    handleSharedDataPageForChildContexts(type, childContainersContexts);

    if (type === ADD_CONTAINER_ITEM || type === MERGE_DATA || type === ADD_SHARED_DATA) {
      const sharedData = type === ADD_SHARED_DATA ? data : data.shared;
      updateSharedPagesUsage(sharedData, context);
    } else if (type === REPLACE_CONTAINER_ITEM) {
      removePreviousSharedDataPageMappings(context);
      updateSharedPagesUsage(data.shared, context);
    } else if (type === REMOVE_CONTAINER_ITEM) {
      removePreviousSharedDataPageMappings(context);
    }
  });
  next(action);
};

export default DataPageMiddleware;
